import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AdminSideBar from "./AdminSideBar";
import { getDailyUpdatesApi } from "../../redux/apiCalls/dailyUpdateApiCall";

function AdminDailyUpdatesToday() {
  const dispatch = useDispatch();
  const { todayFeed, loading, error } = useSelector((state) => state.dailyUpdate);

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);
  const yesterday = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
  }, []);

  const [fromDate, setFromDate] = useState(yesterday);
  const [toDate, setToDate] = useState(today);

  const items = todayFeed?.dailyUpdates || [];

  const handleView = () => {
    dispatch(getDailyUpdatesApi({ from: fromDate, to: toDate }));
  };

  useEffect(() => {
    // initial load: yesterday -> today
    dispatch(getDailyUpdatesApi({ from: fromDate, to: toDate }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const formatWorkDate = (dateStr) => {
    if (!dateStr) return "—";
    // dateStr is YYYY-MM-DD; add midday to avoid timezone shifting
    return new Date(dateStr + "T12:00:00").toLocaleDateString("en-GB", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCreatedAt = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const csvEscape = (value) => {
    const str = value === null || value === undefined ? "" : String(value);
    // wrap in quotes and escape quotes for safe CSV
    return `"${str.replace(/"/g, '""')}"`;
  };

  const downloadCsv = (filename, headers, rows) => {
    const csvRows = [
      headers.map(csvEscape).join(","),
      ...rows.map((r) => r.map(csvEscape).join(",")),
    ].join("\r\n");

    const blob = new Blob([csvRows], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportCsv = () => {
    const headers = [
      "Work Date",
      "Created At",
      "User Name",
      "Email",
      "Task",
      "Done",
      "Notes",
    ];

    const rows = items.map((row) => {
      const createdAtIso = row?.createdAt ? new Date(row.createdAt).toISOString() : "";
      return [
        row.date || "",
        createdAtIso,
        row.userId?.userName || "",
        row.userId?.email || "",
        row.task || "",
        row.done || "",
        row.notes || "",
      ];
    });

    const filename = `daily_updates_${fromDate}_to_${toDate}.csv`;
    downloadCsv(filename, headers, rows);
  };

  return (
    <main className="admin-page">
      <div className="admin-layout">
        <AdminSideBar />

        <section className="admin-main">
          <header className="admin-main-header">
            <h2 className="admin-main-title">Daily Updates (Admin)</h2>
            <p className="admin-main-subtitle">
              Showing from <strong>{fromDate}</strong> to <strong>{toDate}</strong>
            </p>
          </header>

          <div className="report-filters" style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div className="filter-group">
                <label>From</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label>To</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              <div className="filter-actions" style={{ display: "flex", gap: 10 }}>
                <button type="button" className="btn primary" onClick={handleView}>
                  View
                </button>

                <button
                  type="button"
                  className="btn success"
                  onClick={handleExportCsv}
                  disabled={items.length === 0}
                  title={items.length === 0 ? "No data to export" : "Export current results as CSV"}
                >
                  Export CSV
                </button>
              </div>
            </div>
          </div>

          {loading && <p>Loading...</p>}
          {error && <p style={{ color: "red" }}>{error}</p>}

          <table className="report-table">
            <thead>
              <tr>
                <th>Work Date</th>
                <th>Created At</th>
                <th>User</th>
                <th>Email</th>
                <th>Task</th>
                <th>Done</th>
                <th>Notes</th>
              </tr>
            </thead>

            <tbody>
              {items.length > 0 ? (
                items.map((row) => (
                  <tr key={row._id}>
                    <td>{formatWorkDate(row.date)}</td>
                    <td>{formatCreatedAt(row.createdAt)}</td>

                    <td>{row.userId?.userName ?? "—"}</td>
                    <td>{row.userId?.email ?? "—"}</td>

                    <td style={{ whiteSpace: "pre-wrap", maxWidth: 320 }}>
                      {row.task ?? "—"}
                    </td>
                    <td style={{ whiteSpace: "pre-wrap", maxWidth: 320 }}>
                      {row.done ?? "—"}
                    </td>
                    <td style={{ whiteSpace: "pre-wrap", maxWidth: 280 }}>
                      {row.notes || "—"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    No updates found for this range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </main>
  );
}

export default AdminDailyUpdatesToday;